import React, { useState, useEffect } from 'react';
import axios from 'axios';
import '../styles/UserManagement.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const ALL_ROLES = [
  { id: 'user', label: 'User' },
  { id: 'user_manager', label: 'User Manager' },
  { id: 'message_reader', label: 'Message Reader' },
  { id: 'message_sender', label: 'Message Sender' },
  { id: 'broadcast_sender', label: 'Broadcast Sender' },
  { id: 'template_editor', label: 'Template Editor' },
  { id: 'attendance_manager', label: 'Attendance Manager' },
  { id: 'receives_reminders', label: 'Receives Reminders' },
  { id: 'schedule_editor', label: 'Schedule Editor' },
  { id: 'admin', label: 'Admin' },
];

const emptyForm = { username: '', first_name: '', last_name: '', email: '', phone_number: '', password: '', roles: ['user', 'receives_reminders'] };

function UserManagement({ user, setUser }) {
  const [members, setMembers] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [statusMsg, setStatusMsg] = useState('');
  const [loading, setLoading] = useState(true);
  const [selfEdit, setSelfEdit] = useState(null);
  const [selfEditPassword, setSelfEditPassword] = useState('');
  const [selfEditError, setSelfEditError] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [roleEdit, setRoleEdit] = useState(null);
  const [roleEditError, setRoleEditError] = useState('');
  const [userEdit, setUserEdit] = useState(null); // { profileId, name, first_name, last_name, email, phone_number }
  const [userEditError, setUserEditError] = useState('');
  const [resetPwd, setResetPwd] = useState(null); // { profileId, name }
  const [resetPwdValue, setResetPwdValue] = useState('');
  const [resetPwdError, setResetPwdError] = useState('');

  const myRoles = user?.roles || [];
  const isAdmin = myRoles.includes('admin');
  const isManager = myRoles.includes('user_manager') || isAdmin;

  useEffect(() => { fetchMembers(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchMembers = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/users/`);
      const sorted = res.data.slice().sort((a, b) => {
        const nameA = `${a.user.first_name} ${a.user.last_name}`.trim() || a.user.username;
        const nameB = `${b.user.first_name} ${b.user.last_name}`.trim() || b.user.username;
        return nameA.localeCompare(nameB);
      });
      setMembers(sorted);
    } catch (err) {
      console.error('Error fetching users:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_BASE_URL}/users/`, form);
      setStatusMsg('User created successfully.');
      setForm(emptyForm);
      fetchMembers();
    } catch (err) {
      setStatusMsg('Error: ' + (err.response?.data?.error || err.message));
    }
  };

  const openUserEdit = (m) => {
    setUserEdit({
      profileId: m.id,
      name: memberName(m),
      username: m.user.username || '',
      first_name: m.user.first_name || '',
      last_name: m.user.last_name || '',
      email: m.user.email || '',
      phone_number: m.phone_number || '',
    });
    setUserEditError('');
  };

  const saveUserEdit = async () => {
    setUserEditError('');
    try {
      await axios.patch(`${API_BASE_URL}/users/${userEdit.profileId}/update_user/`, {
        user: { username: userEdit.username, first_name: userEdit.first_name, last_name: userEdit.last_name, email: userEdit.email },
        phone_number: userEdit.phone_number,
      });
      setUserEdit(null);
      setStatusMsg('User details updated.');
      fetchMembers();
    } catch (err) {
      setUserEditError(err.response?.data?.error || 'Failed to save changes.');
    }
  };

  const openRoleEdit = (m) => {
    setRoleEdit({ profileId: m.id, name: memberName(m), roles: [...m.roles] });
    setRoleEditError('');
  };

  const toggleRoleInEdit = (roleId) => {
    setRoleEdit((prev) => ({
      ...prev,
      roles: prev.roles.includes(roleId)
        ? prev.roles.filter((r) => r !== roleId)
        : [...prev.roles, roleId],
    }));
  };

  const saveRoles = async () => {
    setRoleEditError('');
    try {
      await axios.post(`${API_BASE_URL}/users/${roleEdit.profileId}/set_roles/`, { roles: roleEdit.roles });
      setRoleEdit(null);
      setStatusMsg('Roles updated.');
      fetchMembers();
    } catch (err) {
      setRoleEditError(err.response?.data?.error || 'Failed to save roles.');
    }
  };

  const openResetPwd = (m) => {
    setResetPwd({ profileId: m.id, name: memberName(m) });
    setResetPwdValue('');
    setResetPwdError('');
  };

  const saveResetPwd = async (e) => {
    e.preventDefault();
    setResetPwdError('');
    try {
      await axios.post(`${API_BASE_URL}/users/${resetPwd.profileId}/reset_password/`, { password: resetPwdValue });
      setResetPwd(null);
      setStatusMsg('Password reset successfully.');
    } catch (err) {
      setResetPwdError(err.response?.data?.error || 'Failed to reset password.');
    }
  };

  const handleDeleteUser = async (profileId) => {
    if (!window.confirm('Delete this user permanently?')) return;
    try {
      await axios.delete(`${API_BASE_URL}/users/${profileId}/`);
      setStatusMsg('User deleted.');
      fetchMembers();
    } catch (err) {
      setStatusMsg('Error deleting user.');
    }
  };

  const openSelfEdit = () => {
    setSelfEdit({
      first_name: user.user?.first_name || '',
      last_name: user.user?.last_name || '',
      email: user.user?.email || '',
      phone_number: user.phone_number || '',
    });
    setSelfEditPassword('');
    setSelfEditError('');
  };

  const handleSelfEdit = async (e) => {
    e.preventDefault();
    setSelfEditError('');
    try {
      const res = await axios.patch(`${API_BASE_URL}/users/update_me/`, {
        current_password: selfEditPassword,
        user: { first_name: selfEdit.first_name, last_name: selfEdit.last_name, email: selfEdit.email },
        phone_number: selfEdit.phone_number,
      });
      setUser(res.data);
      setSelfEdit(null);
      setStatusMsg('Your profile has been updated.');
      fetchMembers();
    } catch (err) {
      setSelfEditError(err.response?.data?.error || 'Update failed.');
    }
  };

  const handleSelfDelete = async (e) => {
    e.preventDefault();
    setDeleteError('');
    try {
      await axios.delete(`${API_BASE_URL}/users/delete_me/`, { data: { current_password: deletePassword } });
      localStorage.removeItem('authToken');
      window.location.reload();
    } catch (err) {
      setDeleteError(err.response?.data?.error || 'Delete failed.');
    }
  };

  const memberName = (m) =>
    `${m.user.first_name} ${m.user.last_name}`.trim() || m.user.username;

  const myProfile = members.find((m) => m.user.id === user?.user?.id);

  if (loading) return <div className="loading">Loading...</div>;

  return (
    <div className="user-management">
      <h2>User Management</h2>
      {statusMsg && <div className="status-msg">{statusMsg}</div>}

      {myProfile && (
        <div className="self-section">
          <h3>My Profile</h3>
          <p>
            <strong>{memberName(myProfile)}</strong> &middot; {myProfile.user.email} &middot; {myProfile.phone_number}
          </p>
          <div className="self-actions">
            <button className="btn-edit" onClick={openSelfEdit}>Edit My Info</button>
            <button className="btn-delete" onClick={() => setConfirmDelete(true)}>Delete My Account</button>
          </div>
        </div>
      )}

      {selfEdit && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>Edit My Profile</h3>
            <form onSubmit={handleSelfEdit}>
              {selfEditError && <div className="error-msg">{selfEditError}</div>}
              <input placeholder="First name" value={selfEdit.first_name} onChange={(e) => setSelfEdit({ ...selfEdit, first_name: e.target.value })} />
              <input placeholder="Last name" value={selfEdit.last_name} onChange={(e) => setSelfEdit({ ...selfEdit, last_name: e.target.value })} />
              <input type="email" placeholder="Email" value={selfEdit.email} onChange={(e) => setSelfEdit({ ...selfEdit, email: e.target.value })} />
              <input placeholder="Phone number" value={selfEdit.phone_number} onChange={(e) => setSelfEdit({ ...selfEdit, phone_number: e.target.value })} />
              <input type="password" placeholder="Current password (required)" value={selfEditPassword} onChange={(e) => setSelfEditPassword(e.target.value)} required />
              <div className="form-actions">
                <button type="submit" className="btn-save">Save</button>
                <button type="button" className="btn-cancel" onClick={() => setSelfEdit(null)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {confirmDelete && (
        <div className="modal-overlay">
          <div className="modal modal-danger">
            <h3>Delete My Account</h3>
            <p>This will permanently delete your account and all associated data. This cannot be undone.</p>
            <form onSubmit={handleSelfDelete}>
              {deleteError && <div className="error-msg">{deleteError}</div>}
              <input type="password" placeholder="Confirm with your current password" value={deletePassword} onChange={(e) => setDeletePassword(e.target.value)} required />
              <div className="form-actions">
                <button type="submit" className="btn-confirm-delete">Delete My Account</button>
                <button type="button" className="btn-cancel" onClick={() => setConfirmDelete(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {isManager && (
        <div className="add-user-section">
          <h3>Add New User</h3>
          <form onSubmit={handleAddUser} className="add-user-form">
            <div className="form-row">
              <input placeholder="First name" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} required />
              <input placeholder="Last name" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} required />
            </div>
            <input placeholder="Username" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} required />
            <input type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            <input placeholder="Phone number (optional)" value={form.phone_number} onChange={(e) => setForm({ ...form, phone_number: e.target.value })} />
            <input type="password" placeholder="Temporary password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required />
            <button type="submit" className="btn-add">Add User</button>
          </form>
        </div>
      )}

      {isManager && (
        <div className="users-table">
          <h3>Existing Users</h3>
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Username</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {members.map((m) => (
                <tr key={m.id}>
                  <td>{memberName(m)}</td>
                  <td>{m.user.username}</td>
                  <td>{m.user.email}</td>
                  <td>{m.phone_number}</td>
                  <td className="action-cell">
                    <button className="btn-edit" onClick={() => openUserEdit(m)}>Edit</button>
                    <button className="btn-edit" onClick={() => openRoleEdit(m)}>Roles</button>
                    <button className="btn-edit" onClick={() => openResetPwd(m)}>Reset Password</button>
                    {m.user.id !== user?.user?.id && (
                      <button className="btn-delete" onClick={() => handleDeleteUser(m.id)}>Delete</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {userEdit && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>Edit: {userEdit.name}</h3>
            {userEditError && <div className="error-msg">{userEditError}</div>}
            <input placeholder="Username" value={userEdit.username} onChange={(e) => setUserEdit({ ...userEdit, username: e.target.value })} />
            <input placeholder="First name" value={userEdit.first_name} onChange={(e) => setUserEdit({ ...userEdit, first_name: e.target.value })} />
            <input placeholder="Last name" value={userEdit.last_name} onChange={(e) => setUserEdit({ ...userEdit, last_name: e.target.value })} />
            <input type="email" placeholder="Email" value={userEdit.email} onChange={(e) => setUserEdit({ ...userEdit, email: e.target.value })} />
            <input placeholder="Phone number (optional)" value={userEdit.phone_number} onChange={(e) => setUserEdit({ ...userEdit, phone_number: e.target.value })} />
            <div className="form-actions">
              <button className="btn-save" onClick={saveUserEdit}>Save</button>
              <button className="btn-cancel" onClick={() => setUserEdit(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {roleEdit && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>Roles: {roleEdit.name}</h3>
            {roleEditError && <div className="error-msg">{roleEditError}</div>}
            <div className="role-checkboxes-modal">
              {ALL_ROLES.filter((r) => r.id !== 'admin' || isAdmin).map((r) => (
                <label key={r.id} className="role-check">
                  <input
                    type="checkbox"
                    checked={roleEdit.roles.includes(r.id)}
                    onChange={() => toggleRoleInEdit(r.id)}
                  />
                  {r.label}
                </label>
              ))}
            </div>
            <div className="form-actions">
              <button className="btn-save" onClick={saveRoles}>Save</button>
              <button className="btn-cancel" onClick={() => setRoleEdit(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {resetPwd && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>Reset Password: {resetPwd.name}</h3>
            {resetPwdError && <div className="error-msg">{resetPwdError}</div>}
            <form onSubmit={saveResetPwd}>
              <input
                type="password"
                placeholder="New password"
                value={resetPwdValue}
                onChange={(e) => setResetPwdValue(e.target.value)}
                required
                minLength={6}
              />
              <div className="form-actions">
                <button type="submit" className="btn-save">Save</button>
                <button type="button" className="btn-cancel" onClick={() => setResetPwd(null)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserManagement;
